/* ==========================================================================
   LAST PALLET - scroll world engine

   Scroll drives a camera, not a set of separate animations. One continuous
   floor plane runs the whole length of the flight; the material under the
   camera crossfades from lot to lot at the waypoints, so there is never a
   visible cut between scenes.

   Two stacked planes share an identical background-position at all times.
   The pan value only ever increases, so a crossfade can happen mid-flight
   without the ground appearing to jump or rewind.

   Vanilla, no dependencies, no build step. Drops into a static page or a
   Shopify section unchanged.
   ========================================================================== */

(function () {
  'use strict';

  /* A section can legitimately appear more than once on a page, and each
     copy emits its own <script> tag. Bind only once so listeners and
     rAF loops are never doubled up. */
  if (window.__lpScrollWorld) { return; }
  window.__lpScrollWorld = true;

  var PAN_DISTANCE = 6400;   // px of ground travelled across the whole flight
  var FADE_BAND = 0.24;      // share of each waypoint spent crossfading
  var TILT_START = 76;       // camera pitch at the start, degrees
  var TILT_END = 71;         // camera pitch at the end (a gentle descent)

  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

  function smoothstep(t) {
    t = clamp(t, 0, 1);
    return t * t * (3 - 2 * t);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isCompact() {
    return window.matchMedia('(max-width: 780px)').matches;
  }

  function initWorld(root) {
    var stage = root.querySelector('.world__stage');
    var track = root.querySelector('.world__track');
    var planeA = root.querySelector('.world__plane--base');
    var planeB = root.querySelector('.world__plane--next');
    var progress = root.querySelector('.world__progress i');
    var captions = Array.prototype.slice.call(root.querySelectorAll('.world__caption'));
    var railItems = Array.prototype.slice.call(root.querySelectorAll('.world__rail-item'));

    if (!stage || !track || !planeA || !planeB) { return; }

    var tiles = [];
    try {
      tiles = JSON.parse(root.getAttribute('data-tiles') || '[]');
    } catch (err) {
      tiles = [];
    }
    if (tiles.length === 0) { return; }

    var count = tiles.length;
    var ticking = false;
    var running = false;
    var lastWidth = window.innerWidth;
    var currentBand = -1;
    var currentActive = -1;

    // Preload every tile so a waypoint never fades into an empty layer.
    tiles.forEach(function (src) {
      var img = new Image();
      img.decoding = 'async';
      img.src = src;
    });

    function setTrackHeight() {
      // Previously one full viewport of dwell per lot, which for seven lots
      // meant 7.75 screens of forced scrolling before the page moved on -
      // impressive once, tedious after that, and it read as a tech demo
      // rather than a storefront. Now the whole flight is held to roughly
      // 1.5-2.2 viewports no matter how many lots are in it: the camera
      // still pushes and the materials still crossfade, there's just far
      // less scrolling between each beat.
      var vh = 1.4 + (count * 0.12);
      if (vh < 1.5) { vh = 1.5; }
      if (vh > 2.2) { vh = 2.2; }
      track.style.height = (vh * 100) + 'vh';
    }

    function paint() {
      ticking = false;

      var rect = track.getBoundingClientRect();
      var scrollable = track.offsetHeight - stage.offsetHeight;
      if (scrollable <= 0) { return; }

      var p = clamp(-rect.top / scrollable, 0, 1);

      // --- the flight itself: monotonic, never rewinds ---
      var pan = p * PAN_DISTANCE;
      var tilt = TILT_START + (TILT_END - TILT_START) * p;

      root.style.setProperty('--world-pan', pan.toFixed(1) + 'px');
      root.style.setProperty('--world-tilt', tilt.toFixed(2) + 'deg');
      root.style.setProperty('--world-progress', (p * 100).toFixed(2) + '%');

      // --- which lot the camera is over, and how far into the crossfade ---
      var scaled = p * count;
      var band = clamp(Math.floor(scaled), 0, count - 1);
      var within = scaled - band;

      var mix = 0;
      if (band < count - 1 && within > 1 - FADE_BAND) {
        mix = smoothstep((within - (1 - FADE_BAND)) / FADE_BAND);
      }

      // The planes always hold band and band+1, but the copy has to follow
      // whichever material is actually dominant on screen. Switching the label
      // at the band edge would name the previous lot for the whole crossfade.
      var active = mix > 0.5 ? Math.min(band + 1, count - 1) : band;

      if (band !== currentBand) {
        currentBand = band;
        planeA.style.backgroundImage = 'url("' + tiles[band] + '")';
        planeB.style.backgroundImage = 'url("' + tiles[Math.min(band + 1, count - 1)] + '")';
      }

      if (active !== currentActive) {
        currentActive = active;
        captions.forEach(function (el, i) {
          el.classList.toggle('is-live', i === active);
        });
        railItems.forEach(function (el, i) {
          el.classList.toggle('is-live', i === active);
          el.setAttribute('aria-current', i === active ? 'true' : 'false');
        });
      }

      root.style.setProperty('--world-mix', mix.toFixed(3));

      if (progress) { progress.style.width = (p * 100).toFixed(2) + '%'; }
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(paint);
      }
    }

    function start() {
      if (running) { return; }
      running = true;
      setTrackHeight();
      window.addEventListener('scroll', onScroll, { passive: true });
      paint();
    }

    function stop() {
      if (!running) { return; }
      running = false;
      window.removeEventListener('scroll', onScroll);
      track.style.height = '';
      root.style.removeProperty('--world-mix');
      currentBand = -1;
      currentActive = -1;
    }

    function evaluate() {
      if (prefersReducedMotion() || isCompact()) { stop(); } else { start(); }
    }

    // A height-only resize on touch is the URL bar showing or hiding. Reacting
    // to it makes the page jump mid-scroll, so only width changes count.
    window.addEventListener('resize', function () {
      var widthChanged = window.innerWidth !== lastWidth;
      lastWidth = window.innerWidth;
      if (!widthChanged && 'ontouchstart' in window) { return; }
      evaluate();
      if (running) { setTrackHeight(); paint(); }
    }, { passive: true });

    window.addEventListener('orientationchange', function () {
      window.setTimeout(function () { evaluate(); if (running) { paint(); } }, 250);
    });

    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.addEventListener) { mq.addEventListener('change', evaluate); }

    evaluate();
  }

  // Reveal-on-scroll now lives in its own file (reveal.js) so every page
  // gets it, not just pages with a [data-world] section. Left out here on
  // purpose - see reveal.js for why.

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-world]'), initWorld);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
